import { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const VALID_POSITIONS = ["portero", "defensa", "centrocampista", "delantero"];

const toText = (players) =>
  players
    .map((p) =>
      [p.number, p.name, p.position, p.age ?? "", p.height ?? "", p.career ?? ""].join("|")
    )
    .join("\n");

const toPlayers = (text) =>
  text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [number, name, position, age, height, career] = l.split("|").map((x) => (x || "").trim());
      return {
        number: Number(number) || 0,
        name,
        position: VALID_POSITIONS.includes(position.toLowerCase()) ? position.toLowerCase() : "delantero",
        age: age ? Number(age) : null,
        height: height || null,
        career: career || null,
      };
    })
    .filter((p) => p.name);

const SquadManager = () => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get("/players").then(({ data }) => setText(toText(data))).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await API.put("/admin/players", { players: toPlayers(text) });
      toast.success(`${data.count} jugadores guardados. Ya se ven en /plantilla`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 max-w-2xl space-y-6" data-testid="squad-manager">
      <div className="rounded-2xl border border-edge bg-ink2 p-4">
        <p className="flex items-center gap-2 font-display text-lg font-bold uppercase text-white">
          <Users className="h-4 w-4 text-crimson" /> Plantilla del Sporting
        </p>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">
          Un jugador por línea:{" "}
          <span className="font-mono-score text-white">dorsal|nombre|posición|edad|altura|trayectoria</span>.
          Edad, altura y trayectoria son opcionales (se ven al pulsar la ficha del jugador).
          Posiciones: <span className="font-mono-score text-glow">portero, defensa, centrocampista, delantero</span>.
        </p>
      </div>
      <textarea
        rows={18}
        data-testid="squad-editor"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"1|Rubén Yáñez|portero|32|1,91 m|Canterano rojiblanco"}
        className="w-full rounded-xl border border-edge bg-ink2 px-3 py-2 font-mono-score text-sm text-white placeholder:text-slate-500 focus:border-crimson focus:outline-none"
      />
      <Button
        onClick={save}
        disabled={busy}
        data-testid="squad-save"
        className="rounded-full bg-crimson px-8 py-3 font-display text-base font-bold uppercase tracking-wider hover:bg-glow"
      >
        {busy ? "Guardando..." : "Guardar plantilla"}
      </Button>
    </div>
  );
};

export default SquadManager;
